(() => {
  if (customElements.get('free-shipping-component')) return;
  const components = new Set();
  let currentCart, pending, revision = 0, refreshQueued = false;
  const validCart = (cart) => Number.isFinite(cart?.total_price) && typeof cart?.currency === 'string';
  function getCart() {
    if (pending) return pending;
    const run = revision;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    const root = (window.routes?.root_url || '/').replace(/\/?$/, '/');
    const request = fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error('Cart HTTP ' + response.status); return response.json(); })
      .then((cart) => {
        if (!validCart(cart)) throw new Error('Invalid cart response');
        if (run !== revision) return null;
        currentCart = cart; return cart;
      }).finally(() => {
        clearTimeout(timer);
        if (pending === request) pending = null;
        if (refreshQueued) { refreshQueued = false; refresh(); }
      });
    pending = request;
    return request;
  }
  function refresh(event) {
    const data = event?.detail?.cartData || event?.detail || event?.cartData;
    if (validCart(data)) { ++revision; refreshQueued = false; currentCart = data; components.forEach((node) => node.render(data)); return; }
    // An add response is not a complete cart. Discard any read started before it,
    // then coalesce intervening events into one fresh read (never abort a cart write).
    if (event) {
      ++revision;
      if (pending) { refreshQueued = true; return; }
    }
    getCart().then((cart) => { if (cart) components.forEach((node) => node.render(cart)); }).catch(() => {
      // A nonessential shipping widget must not block cart or checkout.
      components.forEach((node) => { if (!currentCart) node.hidden = true; });
    });
  }
  function convert(amount, from, to) {
    if (from === to) return amount;
    // Never infer a merchant shipping threshold from a browser FX preference.
    return NaN;
  }
  class FreeShippingComponent extends HTMLElement {
    connectedCallback() {
      components.add(this);
      if (currentCart) this.render(currentCart); else refresh();
    }
    disconnectedCallback() { components.delete(this); }
    render(cart) {
      const config = window.SNMarket?.config;
      const currency = cart.currency;
      let threshold = Number(config?.thresholds?.[currency]);
      if (!Number.isFinite(threshold)) threshold = convert(Number(config?.baseThreshold ?? window.free_shipping_price), config?.baseCurrency || window.free_shipping_default_currency, currency);
      let totalPrice = cart.total_price;
      if (document.body.classList.contains('setup_shipping_delivery') && Array.isArray(cart.items)) {
        totalPrice -= cart.items.reduce((sum, item) => sum + (item.gift_card ? Number(item.final_line_price || 0) : 0), 0);
      }
      const total = convert(Math.max(0, totalPrice) / 100, cart.currency, currency);
      const message = this.querySelector('[data-shipping-message]');
      const progress = this.querySelector('[data-shipping-progress]');
      if (!message || !progress || !Number.isFinite(total) || !(threshold > 0)) { this.hidden = true; this.style.display = 'none'; return; }
      this.hidden = false; this.style.display = 'block';
      const percentage = Math.max(0, Math.min(100, total / threshold * 100));
      const strings = window.snShippingStrings || {};
      const format = (amount) => {
        try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount); }
        catch (_) { return currency + ' ' + amount.toFixed(2); }
      };
      message.replaceChildren();
      const text = (value) => message.appendChild(document.createTextNode(value || ''));
      const money = (value) => {
        // Already formatted in the active currency: do not send through conversion again.
        const span = document.createElement('span'); span.className = 'notranslate'; span.setAttribute('translate', 'no');
        span.textContent = format(value); message.appendChild(span);
      };
      if (total >= threshold) text(strings.qualified);
      else {
        const parts = (total <= 0 ? strings.threshold : strings.remaining).split('__SN_AMOUNT__');
        text(parts[0]); money(total <= 0 ? threshold : Math.max(0, threshold - total)); text(parts.slice(1).join(''));
      }
      progress.classList.toggle('progress-hidden', total <= 0);
      progress.classList.remove('progress-30', 'progress-60', 'progress-100', 'progress-free');
      progress.classList.add(percentage >= 100 ? 'progress-free' : percentage <= 30 ? 'progress-30' : percentage <= 60 ? 'progress-60' : 'progress-100');
      const bar = this.querySelector('[role="progressbar"]');
      bar?.setAttribute('aria-valuemin', '0'); bar?.setAttribute('aria-valuemax', '100'); bar?.setAttribute('aria-valuenow', String(Math.round(percentage)));
      const meter = this.querySelector('[data-free-shipping-progress-meter]');
      if (meter) {
        meter.style.width = percentage + '%';
        const label = meter.querySelector('.text');
        if (label && progress.dataset.textEnabled === 'true') label.textContent = percentage.toFixed(2) + '%';
      }
    }
  }
  customElements.define('free-shipping-component', FreeShippingComponent);
  document.addEventListener('cart:updated', refresh);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
  document.addEventListener('sn:market-change', () => { if (currentCart) components.forEach((node) => node.render(currentCart)); });
  window.addEventListener('pageshow', (event) => { if (event.persisted) refresh(event); });
})();
